import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkDeleteLabelsResponse,
  CountLabelsApplicationErrors,
  CountLabelsOptions,
  CountLabelsResponse,
  ItemLabelCreatedEnvelope,
  ItemLabelDeletedEnvelope,
  ItemLabelUpdatedEnvelope,
  Label,
  LabelQuery,
  LabelsQueryBuilder,
  ListLabelsResponse,
  QueryLabelsResponse,
  UpdateLabel,
  bulkDeleteLabels as universalBulkDeleteLabels,
  countLabels as universalCountLabels,
  createLabel as universalCreateLabel,
  deleteLabel as universalDeleteLabel,
  getLabel as universalGetLabel,
  listLabels as universalListLabels,
  queryLabels as universalQueryLabels,
  typedQueryLabels as universalTypedQueryLabels,
  updateLabel as universalUpdateLabel,
} from './restaurants-menus-v1-item-label-item-labels.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function createLabel(httpClient: HttpClient): CreateLabelSignature {
  return (label: Label) =>
    universalCreateLabel(
      label,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateLabelSignature {
  /**
   * > **Note:** The Label API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Creates an item label.
   * @param - Item label details.
   * @returns Item label.
   */
  (label: Label): Promise<NonNullablePaths<Label, `name`, 2>>;
}

export function getLabel(httpClient: HttpClient): GetLabelSignature {
  return (labelId: string) =>
    universalGetLabel(
      labelId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetLabelSignature {
  /**
   * > **Note:** The Labels API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves an item label by ID.
   * @param - Item label ID.
   * @returns Item label.
   */
  (labelId: string): Promise<NonNullablePaths<Label, `name`, 2>>;
}

export function listLabels(httpClient: HttpClient): ListLabelsSignature {
  return () =>
    universalListLabels(
      // @ts-ignore
      { httpClient }
    );
}

interface ListLabelsSignature {
  /**
   * > **Note:** The Label API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves a list of up to 500 item labels.
   */
  (): Promise<
    NonNullablePaths<ListLabelsResponse, `labels` | `labels.${number}.name`, 4>
  >;
}

export function queryLabels(httpClient: HttpClient): QueryLabelsSignature {
  return () =>
    universalQueryLabels(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryLabelsSignature {
  /**
   * Creates a query to retrieve a list of item labels.
   *
   * The `queryLabels()` function builds a query to retrieve a list of item labels and returns a `LabelsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the [`find()`](/item-labels/labels-query-builder/find) function.
   *
   * You can refine the query by chaining `LabelsQueryBuilder` functions onto the query. `LabelsQueryBuilder` functions enable you to filter, sort, and control the results that `queryLabels()` returns.
   *
   * `queryLabels()` runs with the following `LabelsQueryBuilder` defaults, which you can override:
   *
   * * [`limit(100)`](/item-labels/labels-query-builder/limit)
   *
   * The following `LabelsQueryBuilder` functions are supported for `queryLabels()`. For a full description of the item label object, see the object returned for the [`items`](/item-labels/labels-query-result/items) property in `LabelsQueryResult`.
   */
  (): LabelsQueryBuilder;
}

export function typedQueryLabels(
  httpClient: HttpClient
): TypedQueryLabelsSignature {
  return (query: LabelQuery) =>
    universalTypedQueryLabels(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryLabelsSignature {
  /** */
  (query: LabelQuery): Promise<
    NonNullablePaths<QueryLabelsResponse, `labels` | `labels.${number}.name`, 4>
  >;
}

export function countLabels(httpClient: HttpClient): CountLabelsSignature {
  return (options?: CountLabelsOptions) =>
    universalCountLabels(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountLabelsSignature {
  /**
   * > **Note:** The Label API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Retrieves the number of labels that match a specified filter.
   *
   * If a filter isn't passed in the request, the endpoint returns the count of all labels.
   */
  (options?: CountLabelsOptions): Promise<
    NonNullablePaths<CountLabelsResponse, `count`, 2> & {
      __applicationErrorsType?: CountLabelsApplicationErrors;
    }
  >;
}

export function updateLabel(httpClient: HttpClient): UpdateLabelSignature {
  return (_id: string, label: NonNullablePaths<UpdateLabel, `revision`, 2>) =>
    universalUpdateLabel(
      _id,
      label,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateLabelSignature {
  /**
   * > **Note:** The Label API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Updates an item Label.
   *
   * Each time an item label is updated, its revision increments by 1. The existing revision must be included when updating the item labels. This ensures you're working with the latest item labels information, and it prevents unintended overwrites.
   * @param - Item label ID.
   * @returns Updated item label.
   */
  (_id: string, label: NonNullablePaths<UpdateLabel, `revision`, 2>): Promise<
    NonNullablePaths<Label, `name`, 2>
  >;
}

export function deleteLabel(httpClient: HttpClient): DeleteLabelSignature {
  return (labelId: string) =>
    universalDeleteLabel(
      labelId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteLabelSignature {
  /**
   * > **Note:** The Label API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Deletes an item label.
   * @param - ID of the item label.
   */
  (labelId: string): Promise<void>;
}

export function bulkDeleteLabels(
  httpClient: HttpClient
): BulkDeleteLabelsSignature {
  return (ids: string[]) =>
    universalBulkDeleteLabels(
      ids,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteLabelsSignature {
  /**
   * > **Note:** The Label API only works with the Wix Restaurants Menus (New) app. Make sure you have installed this app from the [Wix App Market](https://www.wix.com/app-market/wix-restaurants-menus-new).
   *
   * Deletes multiple item labels at once.
   * @param - Item Label IDs.
   */
  (ids: string[]): Promise<
    NonNullablePaths<
      BulkDeleteLabelsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export const onItemLabelCreated = EventDefinition(
  'wix.restaurants.menus.v1.item_label_created',
  true,
  (event: ItemLabelCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.icon' }],
        },
      ])
    )
)<ItemLabelCreatedEnvelope>();
export const onItemLabelDeleted = EventDefinition(
  'wix.restaurants.menus.v1.item_label_deleted',
  true,
  (event: ItemLabelDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'undefined.icon' }],
        },
      ])
    )
)<ItemLabelDeletedEnvelope>();
export const onItemLabelUpdated = EventDefinition(
  'wix.restaurants.menus.v1.item_label_updated',
  true,
  (event: ItemLabelUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.externalReferenceInfo.lastSyncDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
            { path: 'modifiedFields.externalReferenceInfo.lastSyncDate' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.icon' }, { path: 'modifiedFields.icon' }],
        },
      ])
    )
)<ItemLabelUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  App,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkDeleteLabelsRequest,
  BulkDeleteLabelsResponse,
  BulkDeleteLabelsResult,
  CloneLabelsRequest,
  CloneLabelsResponse,
  CountLabelsOptions,
  CountLabelsRequest,
  CountLabelsResponse,
  CreateLabelRequest,
  CreateLabelResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomTag,
  DeleteLabelRequest,
  DeleteLabelResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  ExternalReferenceInfo,
  File,
  GetLabelRequest,
  GetLabelResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  ItemLabelCreatedEnvelope,
  ItemLabelDeletedEnvelope,
  ItemLabelUpdatedEnvelope,
  ItemMetadata,
  Label,
  LabelQuerySpec,
  LabelsQueryBuilder,
  LabelsQueryResult,
  ListLabelsRequest,
  ListLabelsResponse,
  MessageEnvelope,
  Page,
  Pages,
  QueryLabelsRequest,
  QueryLabelsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  SyncContentToMultilingualRequest,
  SyncContentToMultilingualResponse,
  URI,
  URIs,
  UpdateLabel,
  UpdateLabelRequest,
  UpdateLabelResponse,
  WebhookIdentityType,
  utils,
} from './restaurants-menus-v1-item-label-item-labels.universal.js';
